import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Luggage, Plus, Minus, ChevronDown, ChevronUp } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import clsx from 'clsx';
import { useFormStore } from '../../stores/formData';
import ButtonSpinner from '../../components/ButtonSpinner';

const ProductSection = ({
  legIndex,
  updatingPricing,
  pricingData = null
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const showFooter = false;

  const actions = useFormStore((state) => state.actions);
  const helpers = useFormStore((state) => state.helpers);
  const currentLeg = useFormStore((state) => state.formData.legs[legIndex]);
  
  // Track expanded/collapsed state for each product line
  const [expandedLines, setExpandedLines] = useState({});
  
  // Track product quantities
  const [productQuantities, setProductQuantities] = useState({});

  // Initialize expanded state based on number of product lines
  useEffect(() => {
    if (pricingData?.rates && Array.isArray(pricingData.rates)) {
      const rates = pricingData.rates;
      const initialExpanded = {};
      
      if (rates.length === 1) {
        // Auto-expand if only one product line
        initialExpanded[rates[0].id] = true;
      } else if (rates.length > 1) {
        // Expand first product line if multiple
        initialExpanded[rates[0].id] = true;
      }
      
      setExpandedLines(initialExpanded);
    }
  }, [pricingData]);

  // Format price from cents
  const formatPrice = (priceCents) => {
    return `$${(priceCents / 100).toFixed(2)}`;
  };

  // Toggle product line expansion (only if multiple lines)
  const toggleExpansion = useCallback((lineId) => {
    if (pricingData?.rates && pricingData.rates.length > 1) {
      setExpandedLines(prev => ({
        ...prev,
        [lineId]: !prev[lineId]
      }));
    }
  }, [pricingData]);

  // Update product quantity
  const updateQuantity = useCallback((productId, change) => {
    if (change === 0) return; // No change
    const selectedProducts = currentLeg.selectedProducts || [];
    const legProduct = selectedProducts.find(p => p.product_id === productId);
    if (!legProduct) {
      const product = pricingData?.rates
        .flatMap(rate => rate.products)
        .find(p => p.product_id === productId);
      if (!product) return; // Product not found

      // Add new product with initial quantity
      actions.leg.addProduct(legIndex, { ...product, quantity: Math.max(change, 0) });
      return
    }

    const newQuantity = Math.max((legProduct?.quantity || 0) + change, 0);
    if (newQuantity === 0) {
      // Remove product if quantity is 0
      actions.leg.removeProduct(legIndex, productId);
    } else{
      actions.leg.updateProductField(
        legIndex, productId, 'quantity', newQuantity
      );
    }
  }, [pricingData, legIndex, currentLeg, actions, helpers]);

  // Get total items for a product line for the badge
  const getProductLineItemTotal = useCallback((products) => {
    const selectedProducts = currentLeg?.selectedProducts || [];
    if (selectedProducts.length === 0) return 0;

    return products.reduce((total, product) => {
      const selectedQuantity = selectedProducts.find(p => p.product_id === product.product_id)?.quantity || 0;
      return total + selectedQuantity;
    }, 0);
  }, [currentLeg, legIndex, helpers]);

  // Theme-based styling
  const cardClassName = clsx(
    'rounded-lg py-5 px-2 md:px-6',
    {
      'bg-slate-800 text-white': isDark,
      'bg-slate-200 text-gray-900': !isDark
    }
  );

  const sectionClassName = clsx(
    'px-2 md:px-3 mb-4',
    {
      'bg-slate-700/30': isDark,
      'bg-slate-200': !isDark
    }
  );

  const titleClassName = clsx(
    'text-medium font-semibold flex items-center ml-2 gap-2 mb-4',
    {
      'text-white': isDark,
      'text-gray-900': !isDark
    }
  );

  const iconClassName = clsx({
    'text-slate-400': isDark,
    'text-gray-500': !isDark
  });

  const productCardClassName = (quantity, isSelected) => clsx(
    'rounded-xl p-4 border-2 transition-all duration-200 mb-3',
    {
      // Dark theme
      'border-slate-600 bg-slate-800/50': isDark && !isSelected,
      'border-green-500 bg-green-500/10': isDark && isSelected,
      // Light theme  
      'border-gray-200 bg-gray-50': !isDark && !isSelected,
      'border-green-500 bg-green-50': !isDark && isSelected,
    }
  );

  const productNameClassName = clsx(
    'font-semibold text-base mb-1',
    {
      'text-white': isDark,
      'text-gray-900': !isDark
    }
  );

  const weightClassName = clsx(
    'text-sm',
    {
      'text-slate-400': isDark,
      'text-gray-600': !isDark
    }
  );

  const priceClassName = clsx(
    'text-lg font-bold',
    {
      'text-white': isDark,
      'text-gray-900': !isDark
    }
  );

  const buttonClassName = clsx(
    'w-8 h-8 rounded-full flex items-center justify-center transition-colors',
    {
      'bg-slate-600 hover:bg-slate-500 text-white': isDark,
      'bg-gray-200 hover:bg-gray-300 text-gray-700': !isDark
    }
  );

  const quantityClassName = clsx(
    'w-4 md:w-8 text-center font-semibold',
    {
      'text-white': isDark,
      'text-gray-900': !isDark
    }
  );

  const collapsiblesectionClassName = clsx(
    'w-full flex items-center justify-between p-3 transition-colors text-left rounded-lg mb-2',
    {
      'hover:bg-slate-700/50': isDark,
      'hover:bg-gray-100': !isDark
    }
  );

  const sectionTitleClassName = clsx(
    'font-medium text-sm uppercase tracking-wide',
    {
      'text-slate-300': isDark,
      'text-gray-700': !isDark
    }
  );

  const totalBadgeClassName = clsx(
    'text-xs px-2 py-1 rounded-full',
    {
      'bg-blue-600 text-white': isDark,
      'bg-blue-500 text-white': !isDark
    }
  );

  const selectTextClassName = clsx(
    'text-xs',
    {
      'text-slate-400': isDark,
      'text-gray-500': !isDark
    }
  );

  
  
  // Render dynamic product lines from pricing data
  return (
    <div data-testid={`leg${legIndex}-product-section`} className={cardClassName}>
      <h3 className="text-xl font-semibold ml-3 mb-4">Luggage</h3>
      <h3 className={titleClassName}>
       <Luggage size={20} className={iconClassName} />
        Add items  
      </h3>
      
      <div className="space-y-6">
        <div className={sectionClassName}>
          {updatingPricing && (
            <div className="mb-4 flex items-center justify-center">
              <ButtonSpinner size={20} />
              <span className="ml-2 text-sm">Updating products...</span>
            </div>
          )}
          {pricingData?.rates && Array.isArray(pricingData.rates) && pricingData.rates.map((productLine) => {
            const isExpanded = expandedLines[productLine.id];
            const totalItems = getProductLineItemTotal(productLine.products);
            const hasMultipleLines = pricingData.rates.length > 1;
            
            return (
              <div key={productLine.id}>
                {/* Product Line Header - Only collapsible if multiple lines */}
                {hasMultipleLines && (
                  <button
                    onClick={() => toggleExpansion(productLine.id)}
                    className={collapsiblesectionClassName}
                  >
                    <div className="flex items-center gap-3">
                      <span className={sectionTitleClassName}>
                        {productLine.name}
                      </span>
                      {totalItems > 0 && (
                        <span className={totalBadgeClassName}>
                          {totalItems}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={selectTextClassName}>SELECT</span>
                      {isExpanded ? (
                        <ChevronUp size={16} className={iconClassName} />
                      ) : (
                        <ChevronDown size={16} className={iconClassName} />
                      )}
                    </div>
                  </button>
                )}

                {/* Product List */}
                {(isExpanded || !hasMultipleLines) && (
                  <div className="space-y-3">
                    {productLine.products.map((product) => {
                      const selectedProducts = helpers.getLeg(legIndex)?.selectedProducts || [];
                      const legProduct = selectedProducts.find(p => p.product_id === product.product_id);
                      const quantity = legProduct?.quantity || 0;
                      const isSelected = quantity > 0;
                      const weightStr = `Max Weight: ${product.weight.lbs} lbs.`;
                      
                      return (
                        <div 
                          key={product.product_id} 
                          className={productCardClassName(quantity, isSelected)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h4 className={productNameClassName}>{product.display_name}</h4>
                              <p className={weightClassName}>{weightStr}</p>
                            </div>
                            
                            <div className="flex items-center gap-4">
                              {/* Price */}
                              <div className={priceClassName}>
                                {formatPrice(product.price_cents)}
                              </div>
                              
                              {/* Quantity Controls */}
                              <div className="flex items-center gap-1 md:gap-3">
                                <button
                                  data-testid={`leg${legIndex}-decrease-${product.product_id}-qty`} 
                                  onClick={() => updateQuantity(product.product_id, -1)}
                                  className={buttonClassName}
                                  disabled={quantity === 0}
                                  aria-label={`Decrease ${product.display_name} count`}
                                >
                                  <Minus size={16} />
                                </button>
                                
                                <span className={quantityClassName}>
                                  {quantity}
                                </span>
                                
                                <button
                                  data-testid={`leg${legIndex}-increase-${product.product_id}-qty`} 
                                  onClick={() => updateQuantity(product.product_id, 1)}
                                  className={buttonClassName}
                                  aria-label={`Increase ${product.display_name} count`}
                                >
                                  <Plus size={16} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer with total items */}
      {Object.keys(productQuantities).length > 0 && showFooter && (
        <div className={clsx(
          'mt-6 pt-4 border-t text-sm',
          {
            'border-slate-600 text-slate-300': isDark,
            'border-gray-200 text-gray-600': !isDark
          }
        )}>
          Total items selected: {Object.values(productQuantities).reduce((sum, qty) => sum + qty, 0)}
        </div>
      )}
    </div>
  );
};

ProductSection.propTypes = {
  legIndex: PropTypes.number.isRequired,
  updatingPricing: PropTypes.bool.isRequired,
  pricingData: PropTypes.shape({
    rates: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      products: PropTypes.arrayOf(PropTypes.shape({
        product_id: PropTypes.string.isRequired,
        display_name: PropTypes.string.isRequired,
        weight: PropTypes.shape({
          lbs: PropTypes.number.isRequired,
          kg: PropTypes.number.isRequired
        }).isRequired,
        price_cents: PropTypes.number.isRequired
      })).isRequired
    })).isRequired
  })
};

export default ProductSection;